// /login (new-password-required state) — first-login force-password-change.
//
// Cognito returns NEW_PASSWORD_REQUIRED on the first sign-in of any
// admin-invited tenant user. useAuthFlow transitions status to
// 'new-password-required'; AuthRoutes routes here.
//
// On submit, calls useAuth().submitNewPassword(); the state machine then
// transitions to 'authenticated' (consumer pool MFA is OPTIONAL, so no
// forced MFA setup step).
//
// Ported from packages/admin/src/routes/auth/FirstLoginPasswordScreen.tsx.
// Visual + copy matches the admin side; only shell wrapper differs
// (ConsumerAuthShell instead of AdminAuthShell).

import { useState, type FormEvent } from 'react';
import {
  Alert,
  Button,
  Card,
  DEFAULT_PASSWORD_RULES,
  LangToggle,
  PasswordRequirements,
  TextInput,
} from '@gc-coach/design-system';
import { ConsumerAuthShell, useAuth } from '../../auth/index.js';
import { useI18n } from '../../i18n/index.js';
import styles from './AuthScreens.module.css';

export function FirstLoginPasswordScreen() {
  const { t, tt, lang, setLang } = useI18n();
  const { status, error, submitNewPassword, clearError } = useAuth();

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [touchedConfirm, setTouchedConfirm] = useState(false);

  const submitting = status.kind === 'submitting-new-password';
  const email = status.kind === 'new-password-required' ? status.email : '';
  const allRulesPass = DEFAULT_PASSWORD_RULES.every((r) => r.test(password));
  const confirmMismatch = touchedConfirm && confirm !== '' && password !== confirm;
  const canSubmit =
    !submitting && password.length > 0 && password === confirm && allRulesPass;

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;
    void submitNewPassword(password);
  };

  const bodyParts = tt('firstLogin.body', { email: ' EMAIL ' }).split(' EMAIL ');

  return (
    <ConsumerAuthShell>
      <div className={styles.topBar}>
        <LangToggle value={lang} onChange={setLang} />
      </div>

      <div className={styles.formColumn}>
        <header className={styles.header}>
          <span className={styles.eyebrow}>{t('firstLogin.eyebrow')}</span>
          <h1 className={styles.title}>{t('firstLogin.headline')}</h1>
          <p className={styles.body}>
            {bodyParts[0]}
            <strong className={styles.bodyEmphasis}>{email}</strong>
            {bodyParts[1]}
          </p>
        </header>

        <Card>
          <form onSubmit={onSubmit} className={styles.form} noValidate>
            {error && (
              <Alert severity="danger" onDismiss={clearError}>
                {error.message}
              </Alert>
            )}

            <TextInput
              label={t('firstLogin.newPasswordLabel')}
              type="password"
              name="new-password"
              autoComplete="new-password"
              value={password}
              onChange={(e) => setPassword(e.currentTarget.value)}
              disabled={submitting}
              revealable
              required
              autoFocus
            />

            <TextInput
              label={t('firstLogin.confirmPasswordLabel')}
              type="password"
              name="confirm-password"
              autoComplete="new-password"
              value={confirm}
              onChange={(e) => setConfirm(e.currentTarget.value)}
              onBlur={() => setTouchedConfirm(true)}
              disabled={submitting}
              revealable
              required
              errorMessage={confirmMismatch ? t('firstLogin.errorMismatch') : undefined}
            />

            <PasswordRequirements value={password} />

            <Button
              type="submit"
              kind="brand"
              fullWidth
              loading={submitting}
              disabled={!canSubmit}
            >
              {t('firstLogin.submitButton')}
            </Button>
          </form>
        </Card>
      </div>
    </ConsumerAuthShell>
  );
}
